import React from "react";
import { Button } from "@/components/ui/button";

/**
 * Per-brand crawler tuning knobs.
 *
 * Two independent settings that live on the brand doc and are read by
 * the pipeline discovery / coverage_gate:
 *
 *  - `extra_seed_urls` (textarea, one URL per line) → pushed into the
 *    discovery frontier BEFORE the sitemap crawler runs. Bypasses
 *    JS-heavy nav menus the frontier can't reach.
 *
 *  - `min_coverage_snapshots_override` (numeric) → per-brand floor
 *    for the coverage_gate. `null` means "use global default
 *    PIPELINE_MIN_COVERAGE_SNAPSHOTS". Set to 1/2 for single-page
 *    artisan brands.
 *
 * Extracted from AdminBrandEdit.jsx as part of the 2026-08-17 code
 * review — kept as a pure presentational component so AdminBrandEdit
 * still owns the state + save handler (persisted via
 * PUT /api/admin/brands/{id}/crawler-settings).
 */
export default function BrandCrawlerSettings({
  brand,
  value,           // { extra_seed_urls: string, min_coverage_snapshots_override: string }
  onChange,        // (nextValue) => void
  onSave,          // async () => void
  busy,            // bool — disables the Save button while a save is in flight
}) {
  return (
    <section className="mt-10" data-testid="crawler-settings-section">
      <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
        Crawler tuning
      </h2>
      <div className="bg-white border p-4 space-y-4" style={{ borderColor: "var(--bp-border)" }}>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>
            Extra seed URLs
            <span className="ml-2 lowercase tracking-normal text-[10px]">
              one per line — pushed into discovery before the sitemap crawler
            </span>
          </label>
          <textarea
            rows={4}
            className="w-full border p-2 font-mono text-xs"
            style={{ borderColor: "var(--bp-border)" }}
            value={value.extra_seed_urls}
            onChange={(e) => onChange({ ...value, extra_seed_urls: e.target.value })}
            placeholder={
              `https://${brand?.slug || "example"}.com/about\n` +
              `https://${brand?.slug || "example"}.com/materials\n` +
              `https://${brand?.slug || "example"}.com/care`
            }
            data-testid="crawler-seed-urls"
          />
        </div>
        <div className="flex items-baseline gap-3">
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
            Minimum usable pages (override)
          </label>
          <input
            type="number"
            min="1"
            className="border p-1 w-20 font-mono text-xs"
            style={{ borderColor: "var(--bp-border)" }}
            value={value.min_coverage_snapshots_override}
            onChange={(e) => onChange({ ...value, min_coverage_snapshots_override: e.target.value })}
            placeholder="—"
            data-testid="crawler-min-coverage"
          />
          <span className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
            blank = use the platform default (3). Lower this for brands with a legitimately single-page website.
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={onSave}
            disabled={busy}
            className="rounded-none"
            data-testid="crawler-settings-save"
          >
            {busy ? "Saving…" : "Save crawler settings"}
          </Button>
          <span className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
            Tip: save first, then hit "Force crawl now" above to re-run with the new settings.
          </span>
        </div>
      </div>
    </section>
  );
}
